import React, { useEffect, useState } from 'react';
import api from "../services/api";

const ComparacionInventario = () => {
  const [comparaciones, setComparaciones] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });
  const [filtro, setFiltro] = useState('todos'); // todos, exactos, sobrantes, faltantes

  useEffect(() => {
    cargarComparacion();
  }, []);

  const cargarComparacion = async () => {
    try {
      setLoading(true);
      const res = await api.get('/inventario-fisico/comparacion');
      setComparaciones(res.data.comparaciones);
      setEstadisticas(res.data.estadisticas);
    } catch (error) { // 👈 ¡AQUÍ ESTABA EL ERROR! FALTABA EL {
      console.error('Error al cargar comparación:', error);
      setMensaje({ texto: "❌ Error al cargar comparación", tipo: "error" });
    } finally {
      setLoading(false);
    }
  };

  const generarReporteExcel = async () => {
    try {
      setLoading(true);
      const response = await api.get('/inventario-fisico/comparacion/reporte', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comparacion_Inventario_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMensaje({ texto: "✅ Reporte generado exitosamente", tipo: "success" });
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
    } catch (error) {
      console.error('Error al generar reporte:', error);
      setMensaje({ texto: "❌ Error al generar reporte", tipo: "error" });
    } finally {
      setLoading(false);
    }
  };

  const comparacionesFiltradas = () => {
    switch (filtro) {
      case 'exactos':
        return comparaciones.filter(c => c.estado === 'exacto');
      case 'sobrantes':
        return comparaciones.filter(c => c.estado === 'sobrante');
      case 'faltantes':
        return comparaciones.filter(c => c.estado === 'faltante');
      default:
        return comparaciones;
    }
  };

  const getColorEstado = (estado) => {
    switch (estado) {
      case 'exacto':
        return { bg: '#d1fae5', color: '#065f46', icon: '✅' };
      case 'sobrante':
        return { bg: '#dbeafe', color: '#1e40af', icon: '📈' };
      case 'faltante':
        return { bg: '#fecaca', color: '#991b1b', icon: '📉' };
      default:
        return { bg: '#f3f4f6', color: '#374151', icon: '❓' };
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 Comparación de Inventario</h1>
          <p style={styles.subtitle}>Inventario Físico vs Stock del Sistema</p>
        </div>
        <div style={styles.headerActions}>
          <button 
            style={styles.btnReporte}
            onClick={generarReporteExcel}
            disabled={loading}
          >
            📥 Generar Reporte Excel
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      {estadisticas && (
        <div style={styles.statsContainer}>
          <div style={{...styles.statCard, ...styles.statTotal}}>
            <div style={styles.statIcon}>📦</div>
            <div>
              <div style={styles.statValue}>{estadisticas.total_registros}</div>
              <div style={styles.statLabel}>Total Materiales</div>
            </div>
          </div>
          <div style={{...styles.statCard, ...styles.statExacto}}>
            <div style={styles.statIcon}>✅</div>
            <div>
              <div style={styles.statValue}>{estadisticas.total_exactos}</div>
              <div style={styles.statLabel}>Exactos</div>
            </div>
          </div>
          <div style={{...styles.statCard, ...styles.statSobrante}}>
            <div style={styles.statIcon}>📈</div>
            <div>
              <div style={styles.statValue}>{estadisticas.total_sobrantes}</div>
              <div style={styles.statLabel}>Sobrantes</div>
              <div style={styles.statMoney}>${estadisticas.valor_total_sobrantes.toFixed(2)}</div>
            </div>
          </div>
          <div style={{...styles.statCard, ...styles.statFaltante}}>
            <div style={styles.statIcon}>📉</div>
            <div>
              <div style={styles.statValue}>{estadisticas.total_faltantes}</div>
              <div style={styles.statLabel}>Faltantes</div>
              <div style={styles.statMoney}>${estadisticas.valor_total_faltantes.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tarjeta de Instrucciones */}
      <div style={styles.helpCard}>
        <h4 style={styles.helpTitle}>¿Cómo procesar los ajustes?</h4>
        <p style={styles.helpText}>
          Esta pantalla es solo de **consulta**. Para corregir las diferencias de inventario y mantener la trazabilidad PEPS, debe procesarlas manualmente:
        </p>
        <ul style={styles.helpList}>
          <li><strong>Para FALTANTES (📉):</strong> Vaya a <strong>"Registrar Salida"</strong> y cree una salida por la cantidad faltante, usando el destino "AJUSTE POR MERMA / DAÑO".</li>
          <li><strong>Para SOBRANTES (📈):</strong> Vaya a <strong>"Entrada Factura"</strong> y cree una entrada por la cantidad sobrante, usando el proveedor "AJUSTE POR SOBRANTE".</li>
        </ul>
        <p style={styles.helpText}>
          Ambas transacciones irán a la pantalla de <strong>"Aprobaciones"</strong> del supervisor para mantener el control.
        </p>
      </div>

      {/* Filtros */}
      <div style={styles.filtrosContainer}>
         <button 
          style={{...styles.filtroBtn, ...(filtro === 'todos' && styles.filtroBtnActive)}}
          onClick={() => setFiltro('todos')}
        >
          Todos ({comparaciones.length})
        </button>
        <button 
          style={{...styles.filtroBtn, ...(filtro === 'exactos' && styles.filtroBtnActive)}}
          onClick={() => setFiltro('exactos')}
        >
          ✅ Exactos ({estadisticas?.total_exactos || 0})
        </button>
        <button 
          style={{...styles.filtroBtn, ...(filtro === 'sobrantes' && styles.filtroBtnActive)}}
          onClick={() => setFiltro('sobrantes')}
        >
          📈 Sobrantes ({estadisticas?.total_sobrantes || 0})
        </button>
        <button 
          style={{...styles.filtroBtn, ...(filtro === 'faltantes' && styles.filtroBtnActive)}}
          onClick={() => setFiltro('faltantes')}
        >
          📉 Faltantes ({estadisticas?.total_faltantes || 0})
        </button>
      </div>

      {/* Mensaje */}
      {mensaje.texto && (
        <div
          style={{
            ...styles.message,
            ...(mensaje.tipo === "success" ? styles.messageSuccess : styles.messageError),
          }}
        >
          {mensaje.texto}
        </div>
      )}

      {/* Tabla de comparación (LIMPIA) */}
      <div style={styles.card}>
        <div style={styles.tableHeader}>
          <h3 style={styles.cardTitle}>
            📋 Registros de Comparación ({comparacionesFiltradas().length})
          </h3>
        </div>
        
        {loading && !comparaciones.length ? (
          <div style={styles.loadingMessage}>⏳ Cargando comparación...</div>
        ) : comparacionesFiltradas().length === 0 ? (
          <div style={styles.emptyMessage}>
            📭 No hay registros que mostrar para este filtro
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Descripción</th>
                  <th style={styles.th}>Categoría</th>
                  <th style={styles.th}>Stock Sistema</th>
                  <th style={styles.th}>Cant. Física</th>
                  <th style={styles.th}>Diferencia</th>
                  <th style={styles.th}>%</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Valor Dif.</th>
                  <th style={styles.th}>Responsable</th>
                </tr>
              </thead>
              <tbody>
                {comparacionesFiltradas().map((comp) => {
                  const colorEstado = getColorEstado(comp.estado);
                  return (
                    <tr key={comp.id} style={styles.tr}>
                      <td style={styles.td}><strong>{comp.codigo}</strong></td>
                      <td style={styles.td}>{comp.descripcion}</td>
                      <td style={styles.td}>{comp.categoria}</td>
                      <td style={styles.tdNum}>{comp.stock_sistema}</td>
                      <td style={styles.tdNum}>{comp.cantidad_fisica}</td>
                      <td style={{
                        ...styles.tdNum,
                        fontWeight: 'bold',
                        color: comp.diferencia === 0 ? '#065f46' : comp.diferencia > 0 ? '#1e40af' : '#991b1b'
                      }}>
                        {comp.diferencia > 0 ? '+' : ''}{comp.diferencia}
                      </td>
                      <td style={styles.tdNum}>{comp.porcentaje_diferencia}%</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          background: colorEstado.bg,
                          color: colorEstado.color
                        }}>
                          {colorEstado.icon} {comp.estado.toUpperCase()}
                        </span>
                      </td>
                      <td style={styles.tdNum}>${comp.valor_diferencia.toFixed(2)}</td>
                      <td style={styles.td}>{comp.responsable || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ... (Pega todos tus 'styles' aquí)
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  btnReporte: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    transition: 'all 0.2s ease',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '25px',
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  statTotal: {
    borderLeft: '4px solid #6366f1',
  },
  statExacto: {
    borderLeft: '4px solid #10b981',
  },
  statSobrante: {
    borderLeft: '4px solid #3b82f6',
  },
  statFaltante: {
    borderLeft: '4px solid #ef4444',
  },
  statIcon: {
    fontSize: '32px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
  },
  statLabel: {
    fontSize: '12px',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statMoney: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    marginTop: '4px',
  },
  helpCard: {
    background: '#eff6ff',
    border: '1px solid #3b82f6',
    borderRadius: '12px',
    padding: '20px 25px',
    marginBottom: '25px',
    borderLeft: '4px solid #3b82f6',
  },
  helpTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e40af',
  },
  helpText: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    color: '#1e40af',
    lineHeight: '1.6',
  },
  helpList: {
    margin: '10px 0 15px 20px',
    padding: '0',
    color: '#1e40af',
    fontSize: '14px',
    lineHeight: '1.7',
  },
  filtrosContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  filtroBtn: {
    padding: '10px 20px',
    background: 'white',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filtroBtnActive: {
    background: '#3b82f6',
    borderColor: '#3b82f6',
    color: 'white',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0',
  },
  message: {
    marginBottom: '20px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
  messageSuccess: {
    background: '#d1fae5',
    color: '#065f46',
    border: '1px solid #10b981',
  },
  messageError: {
    background: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #ef4444',
  },
  loadingMessage: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b',
  },
  emptyMessage: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b',
    background: '#f8fafc',
    borderRadius: '8px',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  tableHeaderRow: {
    background: '#1e293b',
    color: 'white',
  },
  th: {
    padding: '12px 8px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #cbd5e1',
  },
  tr: {
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '10px 8px',
    color: '#475569',
  },
  tdNum: {
    padding: '10px 8px',
    color: '#475569',
    textAlign: 'center',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
};

export default ComparacionInventario;